import { Box, Button, IconButton, Typography } from "@mui/material";
import { ExpandMoreOutlined, MenuOutlined } from "@mui/icons-material";
import React from "react";
import { tokens, mono } from "../../../theme";

export interface ContextBarProps {
  title: string;
  /** Rendered in mono under the title: currency, start date, last update. */
  meta?: string;
  /** Branch / department selects, filter chips — whatever the screen needs. */
  filters?: React.ReactNode;
  primaryAction?: { label: string; onClick: () => void };
  /** Opens the nav drawer. Only supplied when the rail is not permanent. */
  onOpenNav?: () => void;
  /** Makes the title the context switcher's trigger. Omitted when the user is
   *  pinned to one context, so it stays a plain heading. */
  onOpenSwitcher?: (anchor: HTMLElement) => void;
}

/** Replaces dashboardHeader.tsx. Every screen gets exactly one of these. */
const ContextBar = (props: ContextBarProps) => {
  const { title, meta, filters, primaryAction, onOpenNav, onOpenSwitcher } = props;

  return (
    <Box
      sx={{
        // Fixed height only once there is room for one row; below md the
        // filters wrap under the title and the bar grows to fit.
        minHeight: tokens.layout.contextBarHeight,
        flex: "none",
        bgcolor: tokens.color.surface,
        borderBottom: `1px solid ${tokens.color.border}`,
        display: "flex",
        alignItems: { xs: "flex-start", md: "center" },
        flexDirection: { xs: "column", md: "row" },
        justifyContent: "space-between",
        gap: { xs: 1, md: 2 },
        px: { xs: 2, md: 3 },
        py: { xs: 1.5, md: 0 }
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, maxWidth: "100%" }}>
        {onOpenNav && (
          <IconButton
            onClick={onOpenNav}
            aria-label="Open navigation"
            edge="start"
            sx={{ width: 48, height: 48, color: tokens.color.body }}
          >
            <MenuOutlined />
          </IconButton>
        )}
        <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          <Box
            component={onOpenSwitcher ? "button" : "div"}
            onClick={onOpenSwitcher ? (e: any) => onOpenSwitcher(e.currentTarget) : undefined}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              minWidth: 0,
              p: 0,
              border: 0,
              background: "none",
              font: "inherit",
              textAlign: "left",
              color: "inherit",
              cursor: onOpenSwitcher ? "pointer" : "default"
            }}
          >
            <Typography variant="h3" noWrap>
              {title}
            </Typography>
            {onOpenSwitcher && (
              <ExpandMoreOutlined sx={{ fontSize: 20, color: tokens.color.muted, flex: "none" }} />
            )}
          </Box>
          {meta && (
            <Typography
              sx={{ fontFamily: mono, fontSize: 11, color: tokens.color.muted }}
              noWrap
            >
              {meta}
            </Typography>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
          maxWidth: "100%"
        }}
      >
        {filters}
        {primaryAction && (
          <>
            <Box
              sx={{
                display: { xs: "none", md: "block" },
                width: "1px",
                height: 24,
                bgcolor: tokens.color.border,
                mx: 0.5
              }}
            />
            <Button variant="contained" onClick={primaryAction.onClick}>
              {primaryAction.label}
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

export default ContextBar;
